/**
 * ProjectHubScreen — "Gestão do Projeto" (hub). Bloco 0.
 *
 * Substitui o placeholder de /project. Consome GET /projects/{id}/journey e
 * renderiza o Mapa da Jornada (6 fases · done/current/locked), os 5 Quality Gates
 * com status REAL (Negócio/Squads persistidos; Tecnologia/Escopo/Final ainda
 * nao_implementado — Blocos 2/4/5) e a aba de Auditoria (timeline_events reais).
 *
 * Lê ?project=<id>; sem ele, usa o projeto mais recente do tenant.
 */
import { MOSTRAR_ENGINEERING_STANDARDS } from '@/lib/engineeringStandards';
import { C } from '@/theme/palette';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigationContext } from '@/navigation';
import { useProjects } from '@/api/projects';
import { useProjectJourney } from '@/api/journey';

// Cada fase/gate do Mapa da Jornada leva à tela correspondente.
const PHASE_ROUTE = {
  definicoes: '/project',
  negocio: '/journey/design-studio',
  tecnologia: '/journey/blueprint',
  squads: '/gates/b',
  ciclos: '/journey/backlog',
  execucao: '/execution/delivery-check',
};
const GATE_ROUTE = {
  negocio: '/gates/negocio',
  tecnologia: '/gates/tecnologia',
  squads: '/gates/b',
  escopo: '/gates/escopo',
  final: '/gates/final',
};

// Todas as telas da jornada, agrupadas por fase — para navegação direta do hub.
// `step` → abre no WIZARD (/journey?step=…), com o stepper para navegar livre entre
// as telas (revisão de projeto existente). `route` → tela standalone (gates/execução).
const PHASE_SCREENS = [
  { phase: 'Fase 1 · Definições', items: [
    { label: 'Projeto & Git (wizard)', step: 'project' },
  ] },
  { phase: 'Fase 2 · Negócio', items: [
    { label: 'Ideação / Contexto / Data (wizard)', step: 'ideation' },
    { label: '🎨 Design Studio (telas de protótipo)', step: 'designStudio' },
    { label: '◆ Gate 1 · Negócio', step: 'gateNegocio' },
  ] },
  { phase: 'Fase 3 · Tecnologia', items: [
    { label: '🧩 Blueprint (escolher tecnologias: C#/Node, bancos…)', step: 'blueprint' },
    { label: '📄 Artefatos (ADRs, C4, Topologia, Terraform)', step: 'artifacts' },
    { label: '♻️ Biblioteca de Componentes', step: 'components' },
    { label: '◆ Gate 2 · Tecnologia', step: 'gateTecnologia' },
  ] },
  { phase: 'Fase 4 · Squads', items: [
    { label: '⧉ Squad Designer', step: 'squad' },
    { label: '◆ Gate 3 · Squads', step: 'gateSquads' },
  ] },
  { phase: 'Fase 5 · Ciclos', items: [
    { label: '📋 Product Backlog', step: 'backlog' },
    { label: '🔁 Ciclos & Sprints', step: 'cycles' },
    { label: '◆ Gate 4 · Escopo', step: 'gateEscopo' },
  ] },
  { phase: 'Fase 6 · Execução', items: [
    { label: '▶ Live Board', route: '/execution/liveboard' },
    { label: '✔ Conferência de Entrega', route: '/execution/delivery-check' },
    { label: '◆ Gate 5 · Final (aceite duplo)', route: '/gates/final' },
    { label: MOSTRAR_ENGINEERING_STANDARDS ? '📦 Output (PR / Compliance / Audit)' : '📦 Output (PR / Código / Audit)', route: '/execution/output' },
  ] },
];

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

const GATE_STYLE = {
  approved:         { label: 'Aprovado',          fg: C.ok,    bg: 'rgba(1,230,228,0.12)' },
  pending:          { label: 'Pendente',          fg: C.warn,  bg: 'rgba(255,176,32,0.12)' },
  rejected:         { label: 'Rejeitado',         fg: C.bad,   bg: 'rgba(255,51,0,0.12)' },
  nao_implementado: { label: 'Não implementado',  fg: C.muted, bg: C.surface2 },
};

const PHASE_STYLE = {
  done:    { fg: C.ok,    bg: 'rgba(1,230,228,0.10)', border: 'rgba(1,230,228,0.30)' },
  current: { fg: C.accent, bg: 'rgba(1,230,228,0.16)', border: C.accent },
  locked:  { fg: C.muted, bg: C.surface,             border: C.border },
};

function fmt(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString('pt-BR'); } catch { return ts; }
}

const STATUS_LABEL = {
  draft: 'Rascunho', configuring: 'Configurando', running: 'Em execução',
  'gate-waiting': 'Aguardando gate', completed: 'Concluído', failed: 'Falhou', archived: 'Arquivado',
};

export function ProjectHubScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = urlProject || projects?.[0]?.id || null;
  const { data, isLoading, isError, error } = useProjectJourney(projectId);
  const [tab, setTab] = useState('mapa');
  const { navigate } = useNavigationContext();
  const go = (path) => { if (path && projectId) navigate(`${path}?project=${projectId}`); };
  // Abre um item da jornada no lugar certo DESTE projeto:
  //  - `step` → wizard em /journey?step=… (abre no passo e libera navegação livre);
  //  - `route` → tela standalone (gates/execução) com ?project=.
  const openItem = (it) => {
    if (!projectId) return;
    if (it.step) {
      const t = data?.projectType ? `&type=${encodeURIComponent(data.projectType)}` : '';
      const s = data?.deliveryScope ? `&scope=${encodeURIComponent(data.deliveryScope)}` : '';
      navigate(`/journey?project=${projectId}&step=${it.step}${t}${s}`);
    } else if (it.route) {
      navigate(`${it.route}?project=${projectId}`);
    }
  };

  // Seletor de projeto — mostra TODOS os projetos (qualquer status) e troca o ?project=.
  const projectSelector = projects.length > 0 ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
      <label htmlFor="project-picker" style={{ fontSize: 12, color: C.dim }}>Projeto:</label>
      <select
        id="project-picker"
        value={projectId ?? ''}
        onChange={(e) => setSearchParams({ project: e.target.value })}
        style={{
          background: C.surface2, color: C.text, border: `1px solid ${C.border}`,
          borderRadius: 6, padding: '7px 10px', fontSize: 13, minWidth: 320, maxWidth: '100%',
        }}
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} · {STATUS_LABEL[p.status] ?? p.status}
          </option>
        ))}
      </select>
      <span style={{ fontSize: 12, color: C.dim }}>{projects.length} projeto(s)</span>
    </div>
  ) : null;

  if (!projectId) {
    return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto disponível. Crie um projeto na Jornada.</div>;
  }
  if (isLoading) {
    return <div className="sp-page">{projectSelector}<div style={{ padding: 32, color: C.dim }}>Carregando a jornada do projeto…</div></div>;
  }
  if (isError) {
    return <div className="sp-page">{projectSelector}<div style={{ padding: 32, color: C.bad }}>Erro ao carregar: {String(error?.message || error)}</div></div>;
  }

  const gatesApproved = (data.gates || []).filter((g) => g.status === 'approved').length;

  // Análise: a jornada NÃO passa pela fase de Tecnologia nem pelo Design Studio.
  // Esconde essas telas/fase no hub para não navegar até lá (Gate 2 é auto-aprovado).
  const isAnalise = data.deliveryScope === 'analise';
  const hubPhases = (data.phases || []).filter((p) => !(isAnalise && p.id === 'tecnologia'));
  const phaseScreens = isAnalise
    ? PHASE_SCREENS
        .filter((grp) => !/Tecnologia/i.test(grp.phase))
        .map((grp) => ({ ...grp, items: grp.items.filter((it) => it.step !== 'designStudio') }))
    : PHASE_SCREENS;

  return (
    <div className="sp-page" style={{ color: C.text, maxWidth: 980 }}>
      {projectSelector}

      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>{data.name}</h2>
        <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
          background: C.surface2, color: C.dim, border: `1px solid ${C.border}` }}>
          {data.projectType}
        </span>
        <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
          background: 'rgba(1,230,228,0.10)', color: C.accent }}>
          {data.status}
        </span>
      </div>

      {/* KPIs simples */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginTop: 18 }}>
        <Kpi v={`${gatesApproved} de 5`} l="Gates aprovados" />
        <Kpi v={data.phases.find((p) => p.status === 'current')?.label ?? '—'} l="Fase atual" />
        <Kpi v={String((data.audit || []).length)} l="Eventos de auditoria" />
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: 6, marginTop: 26, borderBottom: `1px solid ${C.border}` }}>
        {[['mapa', 'Mapa da Jornada'], ['auditoria', 'Auditoria & Rastreabilidade'], ['qualidade', 'Qualidade']].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 12px', fontSize: 13.5,
            color: tab === k ? C.text : C.dim,
            borderBottom: tab === k ? `2px solid ${C.accent}` : '2px solid transparent',
          }}>{lbl}</button>
        ))}
      </div>

      {/* MAPA */}
      {tab === 'mapa' && (
        <div style={{ marginTop: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${hubPhases.length}, minmax(0,1fr))`, gap: 10 }}>
            {hubPhases.map((p, i) => {
              const st = PHASE_STYLE[p.status] || PHASE_STYLE.locked;
              return (
                <div key={p.id} onClick={() => go(PHASE_ROUTE[p.id])} title="Abrir esta fase"
                  style={{ border: `1px solid ${st.border}`, background: st.bg, borderRadius: 12, padding: 12,
                    cursor: PHASE_ROUTE[p.id] ? 'pointer' : 'default' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 10.5, color: st.fg }}>FASE {i + 1}</div>
                  <div style={{ fontWeight: 700, fontSize: 13, margin: '6px 0 8px' }}>{p.label}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 10, color: st.fg }}>
                    {p.status === 'done' ? '✓ concluída' : p.status === 'current' ? '● atual' : '· bloqueada'}
                  </div>
                </div>
              );
            })}
          </div>

          <h3 style={{ marginTop: 30, fontSize: 15 }}>Quality Gates</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
            {data.gates.map((g) => {
              const gs = GATE_STYLE[g.status] || GATE_STYLE.pending;
              return (
                <div key={g.key} onClick={() => go(GATE_ROUTE[g.key])} title="Abrir este gate"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                  border: `1px solid ${C.border}`, borderRadius: 10, padding: '11px 14px', background: C.surface }}>
                  <div style={{ fontWeight: 650, fontSize: 13.5, flex: 1 }}>{g.label} <span style={{ color: C.muted, fontSize: 11 }}>→</span></div>
                  {g.waiver && <span style={{ fontFamily: 'monospace', fontSize: 10, color: C.warn }}>waiver</span>}
                  {g.decidedAt && <span style={{ fontFamily: 'monospace', fontSize: 10.5, color: C.muted }}>{fmt(g.decidedAt)}</span>}
                  <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
                    color: gs.fg, background: gs.bg }}>{gs.label}</span>
                </div>
              );
            })}
          </div>

          {/* Telas da Jornada por fase — navegação direta para tudo */}
          <h3 style={{ marginTop: 30, fontSize: 15 }}>Telas da Jornada</h3>
          <p style={{ color: C.dim, fontSize: 12.5, margin: '4px 0 10px' }}>
            Abra qualquer tela da jornada deste projeto (cada uma gera/mostra conteúdo real por IA).
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 12 }}>
            {phaseScreens.map((grp) => (
              <div key={grp.phase} style={{ border: `1px solid ${C.border}`, background: C.surface,
                borderRadius: 12, padding: 14 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 10.5, textTransform: 'uppercase',
                  letterSpacing: '0.08em', color: C.muted, marginBottom: 8 }}>{grp.phase}</div>
                {grp.items.map((it) => (
                  <div key={it.label} onClick={() => openItem(it)}
                    style={{ cursor: 'pointer', padding: '6px 8px', borderRadius: 7, fontSize: 13,
                      color: C.dim, marginBottom: 2 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = C.surface2; e.currentTarget.style.color = C.text; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.dim; }}>
                    {it.label}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AUDITORIA */}
      {tab === 'auditoria' && (
        <div style={{ marginTop: 20 }}>
          {(data.audit || []).length === 0 && (
            <div style={{ color: C.dim }}>Sem eventos de auditoria (execução ainda não iniciada).</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {(data.audit || []).map((e) => (
              <div key={e.seq} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 180px', gap: 12,
                padding: '9px 0', borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ fontFamily: 'monospace', color: C.muted }}>#{e.seq}</span>
                <span style={{ color: C.text }}>{e.eventType}
                  {e.actorType && <span style={{ color: C.muted }}> · {e.actorType}</span>}
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.muted, textAlign: 'right' }}>{fmt(e.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* QUALIDADE */}
      {tab === 'qualidade' && (
        <div style={{ marginTop: 20, color: C.dim }}>
          <p>{gatesApproved} de 5 Quality Gates aprovados. Os gates de <strong style={{ color: C.text }}>Tecnologia</strong>,
            {' '}<strong style={{ color: C.text }}>Escopo</strong> e <strong style={{ color: C.text }}>Final</strong> ainda
            não foram implementados (Blocos 2/4/5 do plano de implementação).</p>
        </div>
      )}
    </div>
  );
}

function Kpi({ v, l }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{v}</div>
      <div style={{ fontFamily: 'monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.muted, marginTop: 4 }}>{l}</div>
    </div>
  );
}
